import styled from '@emotion/styled';
import { Variables } from '../../base';

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const SectionLabel = styled.div`
    font-size: 11px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
`;

export const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const Card = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
    background: ${Variables.ThemeLight.Background3};
    overflow: hidden;
`;

export const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
`;

export const CardTitle = styled.span`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.semibold};
    color: ${Variables.ThemeLight.TextStrong};
    min-width: 0;
`;

export const Explain = styled.div`
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.5;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const RulesList = styled.div`
    display: flex;
    flex-direction: column;
`;

export const RuleRow = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 10px 14px;

    &:not(:first-of-type) {
        border-top: 1px solid ${Variables.ThemeLight.Border2};
    }
`;

export const RuleRowIcon = styled.span<{ $halt?: boolean }>`
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    color: ${props =>
        props.$halt ? Variables.ThemeLight.AttentionConstant6 : Variables.ThemeLight.TextMild};

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const RuleBody = styled.div`
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const RuleTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

export const RuleBadge = styled.span<{ $halt?: boolean }>`
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    padding: 0 7px;
    white-space: nowrap;
    text-transform: uppercase;
    font-family: ${Variables.Fonts.calibre};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: ${props =>
        props.$halt ? Variables.ThemeLight.AttentionConstant6 : Variables.ThemeLight.TextMild};
    background: ${props =>
        props.$halt ? Variables.ThemeLight.AttentionMild1 : Variables.ThemeLight.Background1};
    border-radius: 4px;
`;

export const RuleNumber = styled.span`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.semibold};
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextStrong};
    white-space: nowrap;
`;

export const RuleText = styled.span`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextMedium};
    text-wrap: pretty;
`;

export const RuleLead = styled.strong`
    font-weight: ${Variables.FontWeight.semibold};
    color: ${Variables.ThemeLight.TextStrong};
`;

export const EmptyState = styled.div`
    padding: 10px 14px;
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const LoadingState = styled.div`
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMild};
    font-style: italic;
    padding: 8px 0;
`;

export const MetaFooter = styled.div`
    display: flex;
    border-top: 1px solid ${Variables.ThemeLight.Border2};
`;

export const MetaCell = styled.div<{ $borderLeft?: boolean }>`
    flex: 1;
    padding: 8px 14px;
    border-left: ${props => (props.$borderLeft ? `1px solid ${Variables.ThemeLight.Border2}` : 'none')};
    min-width: 0;
`;

export const MetaLabel = styled.div`
    font-size: 10px;
    font-weight: ${Variables.FontWeight.medium};
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
    margin-bottom: 3px;
`;

export const MetaValue = styled.div`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.semibold};
    color: ${Variables.ThemeLight.TextStrong};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ExplainLabel = styled.span`
    font-size: 10px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
    margin-right: 6px;
`;

export const RawBlock = styled.div`
    padding: 12px 14px;
`;

export const RawDetails = styled.details`
    margin: 0;
`;

export const RawSummary = styled.summary`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
    cursor: pointer;
    margin-bottom: 5px;
    list-style: none;
    user-select: none;

    &::-webkit-details-marker {
        display: none;
    }

    &::before {
        content: '>';
        display: inline-block;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-weight: 700;
        line-height: 1;
        transform: rotate(0deg);
        transition: transform 120ms ease;
    }

    details[open] > &::before {
        transform: rotate(90deg);
    }
`;

export const RawPre = styled.pre`
    margin: 6px 0 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1.5;
    color: ${Variables.ThemeLight.TextMedium};
    background: ${Variables.ThemeLight.Background2};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 6px;
    padding: 8px 10px;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 240px;
    overflow: auto;
`;

export const ProblemsBlock = styled.div`
    padding: 2px 14px 12px;
`;

export const ProblemsHeading = styled.div`
    font-size: 10px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
    margin-bottom: 7px;
`;

export const ProblemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const ProblemRow = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    background: ${Variables.ThemeLight.Background2};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 6px;
    padding: 9px 11px;
`;

export const ProblemIndex = styled.span`
    flex: none;
    width: 18px;
    height: 18px;
    margin-top: 1px;
    border-radius: 4px;
    background: ${Variables.ThemeLight.AttentionMild1};
    color: ${Variables.ThemeLight.AttentionConstant6};
    font-size: 10px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
`;

export const ProblemBody = styled.span`
    min-width: 0;
    flex: 1;
`;

export const ProblemTitleRow = styled.span`
    display: flex;
    align-items: baseline;
    gap: 7px;
    flex-wrap: wrap;
`;

export const ProblemSlot = styled.span`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.semibold};
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const ProblemWho = styled.span`
    font-size: 11px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextMild};
`;

export const ProblemDetailRow = styled.span`
    display: flex;
    align-items: baseline;
    gap: 7px;
    margin-top: 3px;
    flex-wrap: wrap;
`;

export const ProblemValue = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    font-weight: ${Variables.FontWeight.semibold};
    line-height: 1.4;
    color: ${Variables.ThemeLight.WarningInverted6};
    text-decoration: line-through;
`;

export const ProblemWhy = styled.span`
    font-size: 11px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
`;
